import type { ChangeEvent } from "react";

import { SectionHeader } from "@/components/ui";
import { t } from "@/i18n";

import type { DenetimKuralForm } from "./kuralForm";

interface KuralPaneliProps {
  value: DenetimKuralForm;
  onChange: (value: DenetimKuralForm) => void;
  kategoriSecenekleri: string[];
  disabled?: boolean;
}

/** Kategori coklu-secim kutusu — 7 satir yukseklikte (gorev A gorunum iyilestirmesi). */
const KATEGORI_SATIR_SAYISI = 7;

/**
 * Katalog denetimi kural paneli — eski `legacy/denetim.html:42-78` +
 * `legacy/js/denetim.js:42-55 kuralOku` (görev A). "Yeniden tara" düğmesinin
 * ÜSTÜNDE gösterilir (Denetim.tsx); burada yalnız form alanları var, tarama
 * eylemi sayfa seviyesindeki mevcut düğmeye bağlıdır.
 */
export function KuralPaneli({ value, onChange, kategoriSecenekleri, disabled }: KuralPaneliProps) {
  function set<K extends keyof DenetimKuralForm>(key: K, next: DenetimKuralForm[K]) {
    onChange({ ...value, [key]: next });
  }

  function handleKategoriChange(e: ChangeEvent<HTMLSelectElement>) {
    const secili = Array.from(e.target.selectedOptions).map((o) => o.value);
    set("kategoriler", secili);
  }

  return (
    <div className="card space-y-3">
      <SectionHeader title={t("denetim.kural.baslik")} />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className="label">{t("denetim.kural.kelime")}</span>
          <input
            type="text"
            value={value.kelime}
            disabled={disabled}
            onChange={(e) => set("kelime", e.target.value)}
            className="input"
          />
        </label>
        <label className="block">
          <span className="label">{t("denetim.kural.yuzde")}</span>
          <input
            type="number"
            min={1}
            max={100}
            value={value.yuzde}
            disabled={disabled}
            onChange={(e) => set("yuzde", Number(e.target.value) || DEFAULT_YUZDE)}
            className="input"
          />
        </label>
        <label className="block">
          <span className="label">{t("denetim.kural.ozellik_adi")}</span>
          <input
            type="text"
            value={value.ozellik_adi}
            disabled={disabled}
            onChange={(e) => set("ozellik_adi", e.target.value)}
            className="input"
          />
        </label>
        <label className="block">
          <span className="label">{t("denetim.kural.rozet_dosyasi")}</span>
          <input
            type="text"
            value={value.rozet_dosyasi}
            disabled={disabled}
            onChange={(e) => set("rozet_dosyasi", e.target.value)}
            className="input"
          />
        </label>
      </div>

      <label className="block">
        <span className="label">
          {t("denetim.kural.kategoriler")}{" "}
          <span className="text-xs font-normal opacity-70">{t("denetim.kural.kategoriler_yardim")}</span>
        </span>
        <select
          multiple
          size={KATEGORI_SATIR_SAYISI}
          disabled={disabled}
          value={value.kategoriler}
          onChange={handleKategoriChange}
          className="w-full rounded-[9px] border-[1.5px] border-line bg-card px-3 py-2 text-sm text-ink outline-none focus:border-brand-primary disabled:opacity-60"
        >
          {kategoriSecenekleri.map((kategori) => (
            <option key={kategori} value={kategori}>
              {kategori}
            </option>
          ))}
        </select>
      </label>

      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <label className="flex min-h-[44px] items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={value.urun_adi_kontrol}
            disabled={disabled}
            onChange={(e) => set("urun_adi_kontrol", e.target.checked)}
          />
          {t("denetim.kural.urun_adi_kontrol")}
        </label>
        <label className="flex min-h-[44px] items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={value.gri_alan}
            disabled={disabled}
            onChange={(e) => set("gri_alan", e.target.checked)}
          />
          {t("denetim.kural.gri_alan")}
        </label>
        <label className="flex min-h-[44px] items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={value.satisa_kapali_dahil}
            disabled={disabled}
            onChange={(e) => set("satisa_kapali_dahil", e.target.checked)}
          />
          {t("denetim.kural.satisa_kapali_dahil")}
        </label>
      </div>
    </div>
  );
}

const DEFAULT_YUZDE = 100;
